import * as fs from 'node:fs/promises';
import * as path from 'node:path';
import { parseE2eeKeyRecord, serializeE2eeKeyRecord } from '@buildautomaton/e2ee';
import { createE2eeKeyRecord, type CliE2eeKey } from '../lib/e2ee/index.js';

export const E2E_CERTIFICATE_ALGORITHM = 'A1';
const CERTIFICATE_FILE_EXTENSIONS = new Set(['.pem', '.key', '.crt']);

export type E2eCertificate = CliE2eeKey & {
  algorithm: typeof E2E_CERTIFICATE_ALGORITHM;
  pemBundle: string;
};

export interface LoadedE2eCertificates {
  directory: string;
  certificates: E2eCertificate[];
  activeCertificate: E2eCertificate;
  generated: boolean;
}

function certificateFromKey({
  raw,
  name,
  createdAt,
}: {
  raw: string;
  name: string;
  createdAt: string;
}): E2eCertificate | undefined {
  try {
    const key = parseE2eeKeyRecord(raw, name);
    return {
      ...key,
      name: key.name || name,
      algorithm: E2E_CERTIFICATE_ALGORITHM,
      pemBundle: serializeE2eeKeyRecord({ ...key, createdAt: key.createdAt || createdAt }),
    };
  } catch {
    return undefined;
  }
}

function baseNameForCertificate(fileName: string): string {
  return path.basename(fileName, path.extname(fileName)) || 'BuildAutomaton E2EE certificate';
}

async function readCertificateFiles(directory: string): Promise<E2eCertificate[]> {
  let entries;
  try {
    entries = await fs.readdir(directory, { withFileTypes: true });
  } catch (err) {
    const nodeErr = err as NodeJS.ErrnoException;
    if (nodeErr.code === 'ENOENT') return [];
    throw err;
  }

  const certificates: E2eCertificate[] = [];
  for (const entry of entries.sort((a, b) => a.name.localeCompare(b.name))) {
    if (!entry.isFile() || !CERTIFICATE_FILE_EXTENSIONS.has(path.extname(entry.name).toLowerCase())) continue;
    const filePath = path.join(directory, entry.name);
    const raw = await fs.readFile(filePath, 'utf8').catch(() => undefined);
    if (!raw) continue;
    const stat = await fs.stat(filePath).catch(() => undefined);
    const certificate = certificateFromKey({
      raw,
      name: baseNameForCertificate(entry.name),
      createdAt: stat?.mtime?.toISOString() ?? new Date().toISOString(),
    });
    if (certificate) {
      certificates.push(certificate);
    }
  }
  return certificates;
}

function createCertificate(directory: string): E2eCertificate {
  const baseName = path.basename(directory) || 'BuildAutomaton E2EE certificate';
  const key = createE2eeKeyRecord(baseName);
  return {
    ...key,
    algorithm: E2E_CERTIFICATE_ALGORITHM,
    pemBundle: serializeE2eeKeyRecord(key),
  };
}

export async function loadOrCreateE2eCertificates(directory: string): Promise<LoadedE2eCertificates> {
  const resolved = path.resolve(directory);
  await fs.mkdir(resolved, { recursive: true, mode: 0o700 });

  const existing = await readCertificateFiles(resolved);
  if (existing.length > 0) {
    return {
      directory: resolved,
      certificates: existing,
      activeCertificate: existing[0],
      generated: false,
    };
  }

  const certificate = createCertificate(resolved);
  const fileName = `buildautomaton-e2ee-key-${certificate.id.slice(0, 12)}.pem`;
  await fs.writeFile(path.join(resolved, fileName), certificate.pemBundle, {
    mode: 0o600,
  });

  return {
    directory: resolved,
    certificates: [certificate],
    activeCertificate: certificate,
    generated: true,
  };
}
